import React from "react";
import AddAdditionalInterestDialog from "./AddAdditionalInterestDialog";
import GenerateCertificate from "./GenerateCertificate";
import ViewAdditionalInterest, { type AdditionalInterestDetail } from "./ViewAdditionalInterest";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import type { AdditionalInterestItem } from 'features/dashboard/types/dashboard';
import type { ApiAdditionalInterest } from '../hooks/useAdditionalInterests';
import "../../../styles/panels.css";
const INTEREST_TYPE_MAP: { [key: string]: { label: string; color: string } } = {
  additionalInsured: { label: "Additional insured", color: "#0057A4" },
  lossPayee: { label: "Loss Payee", color: "#059669" },
  breachOfWarranty: { label: "Breach of warranty", color: "#f59e42" },
  mortgagee: { label: "Mortgagee", color: "#a21caf" },
  owner: { label: "Owner", color: "#f43f5e" },
  leasebackOwner: { label: "Leaseback owner", color: "#eab308" },
  registrant: { label: "Registrant", color: "#6366f1" },
  lienholder: { label: "Lienholder", color: "#10b981" },
  trustee: { label: "Trustee", color: "#f472b6" },
  certificateHolder: { label: "Certificate Holder", color: "#0891b2" },
  lessor: { label: "Lessor", color: "#7c3aed" },
  registeredOwner: { label: "Registered Owner", color: "#ea580c" },
  other: { label: "Other", color: "#0057A4" },
};

function getInterestBadges(ai: any) {
  const badges = [];
  for (const key in INTEREST_TYPE_MAP) {
    if (ai[key]) {
      let label = INTEREST_TYPE_MAP[key].label;
      if (key === "other" && ai.additionalInterestOtherText) {
        label = ai.additionalInterestOtherText;
      }
      badges.push({ label, color: INTEREST_TYPE_MAP[key].color });
    }
  }
  return badges;
}

export type AdditionalInterestsListProps = {
  items: AdditionalInterestItem[];
  rawItems?: ApiAdditionalInterest[];
  onAddNew?: () => void;
  isLoading?: boolean;
  onRefetch?: () => void;
  insuredDatabaseId?: string;
  insuredEmail?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  insuredCommercialName?: string;
  first?: number;
  rows?: number;
  totalCount?: number;
  searchValue?: string;
  onPageChange?: (first: number) => void;
  onSearchChange?: (val: string) => void;
};

export default function AdditionalInterestsList({
  items,
  rawItems,
  onAddNew,
  isLoading,
  onRefetch,
  insuredDatabaseId,
  insuredEmail,
  insuredFirstName,
  insuredLastName,
  insuredCommercialName,
  first: controlledFirst,
  rows: controlledRows,
  totalCount,
  searchValue,
  onPageChange,
  onSearchChange,
}: AdditionalInterestsListProps) {
  const toastRef = React.useRef<Toast>(null);
  const [internalFirst, setInternalFirst] = React.useState(0);
  const [inputValue, setInputValue] = React.useState(searchValue ?? '');
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [selectedInterest, setSelectedInterest] = React.useState<AdditionalInterestDetail | null>(null);
  const [dialogVisible, setDialogVisible] = React.useState(false);

  // Keep local input in sync when parent clears search (e.g. on insured change)
  React.useEffect(() => {
    setInputValue(searchValue ?? '');
  }, [searchValue]);

  // Debounce: wait 400ms after user stops typing before calling backend
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange?.(inputValue);
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const first = controlledFirst ?? internalFirst;
  const rows = controlledRows ?? 5;
  const effectiveTotal = totalCount ?? items.length;

  const handlePageChange = (f: number) => {
    if (onPageChange) onPageChange(f);
    else setInternalFirst(f);
  };

  const [genCertDialog, setGenCertDialog] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  const handleExportCsv = async () => {
    if (!insuredDatabaseId) return;
    setExporting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/policy-additional-interests-csv?insuredDatabaseId=${insuredDatabaseId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
            NCToken: localStorage.getItem('NCToken') || '',
          },
        }
      );
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'additional-interests.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Export Failed', detail: 'Could not export CSV. Please try again.', life: 3500 });
    } finally {
      setExporting(false);
    }
  };

  const handleInterestClick = (id: string) => {
    const raw = rawItems?.find((r) => r.databaseId === id);
    if (raw) {
      setSelectedInterest(raw as AdditionalInterestDetail);
    } else {
      const mapped = items.find((i) => i.id === id);
      if (mapped) {
        setSelectedInterest({
          databaseId: mapped.id,
          certificateHolderName: mapped.name,
        });
      }
    }
    setDialogVisible(true);
  };

  const handleSaved = () => {
    setShowAddDialog(false);
    toastRef.current?.show({ severity: 'success', summary: 'Saved', detail: 'Additional interest added successfully.', life: 3500 });
    onRefetch?.();
  };

  return (
    <>
    <Toast ref={toastRef} position="top-right" />
    <section className="panel">
      <header className="panel__header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
        <div className="panel__title" style={{ color: '#ba170b', fontWeight: 600, marginBottom: 8 }}>Additional Interests</div>
        <div style={{ position: 'relative', width: '100%', marginBottom: 6 }}>
          <i className="pi pi-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.75rem', pointerEvents: 'none' }} />
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search additional interests..."
            style={{ width: '100%', paddingLeft: 28, paddingRight: inputValue ? 28 : 8, paddingTop: 5, paddingBottom: 5, fontSize: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }}
          />
          {inputValue && (
            <i
              className="pi pi-times"
              onClick={() => setInputValue('')}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.7rem', cursor: 'pointer' }}
            />
          )}
        </div>
        <div className="panel__headerBtns">
          <Button
            label="Generate Certificates"
            icon="pi pi-send"
            className="p-button-sm panel__genCertBtn"
            onClick={() => setGenCertDialog(true)}
          />
          <Button
            label="Export CSV"
            icon="pi pi-download"
            className="p-button-sm panel__genCertBtn"
            onClick={handleExportCsv}
            loading={exporting}
            disabled={exporting || !insuredDatabaseId}
          />
        </div>
      </header>
      <GenerateCertificate visible={genCertDialog} onHide={() => setGenCertDialog(false)} insuredDatabaseId={insuredDatabaseId} />
      <div className="panel__body">
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="interestRow">
                <div className="interestRow__top">
                  <Skeleton width="110px" height="1rem" />
                  <Skeleton width="90px" height="20px" borderRadius="6px" />
                </div>
                <Skeleton
                  width="140px"
                  height="0.8rem"
                  style={{ marginTop: 4 }}
                />
              </div>
            ))
          : items.length === 0 ? (
            <div className="panel__emptyState">
              <div className="panel__emptyState__iconWrap">
                <i className="pi pi-users panel__emptyState__icon" />
              </div>
              <div className="panel__emptyState__title">{inputValue ? 'No results found' : 'No additional interests'}</div>
              <div className="panel__emptyState__sub">{inputValue ? `No interests match "${inputValue}".` : 'Click "Add New" to add an additional interest.'}</div>
            </div>
          ) : items.map((ai) => {
              let badge = null;
              const type = Object.keys(INTEREST_TYPE_MAP).find(
                (k) =>
                  INTEREST_TYPE_MAP[k].label.toLowerCase() ===
                  ai.roleLabel.toLowerCase(),
              );
              if (type) {
                badge = (
                  <span
                    className="interest-badge"
                    style={{
                      background: INTEREST_TYPE_MAP[type].color + "22",
                      color: INTEREST_TYPE_MAP[type].color,
                      border: `1px solid ${INTEREST_TYPE_MAP[type].color}`,
                    }}
                  >
                    {INTEREST_TYPE_MAP[type].label}
                  </span>
                );
              } else if (ai.roleLabel) {
                badge = (
                  <span
                    className="interest-badge"
                    style={{
                      background: "#64748b22",
                      color: "#64748b",
                      border: "1px solid #64748b",
                    }}
                  >
                    {ai.roleLabel}
                  </span>
                );
              }
              return (
                <div
                  key={ai.id}
                  className="interestRow interestRow--clickable"
                  onClick={() => handleInterestClick(ai.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleInterestClick(ai.id); }}
                >
                  <div className="interestRow__top">
                    <div className="interestRow__name">{ai.name}</div>
                    <div style={{ display: "flex", gap: 4 }}>{badge}</div>
                  </div>
                  {ai.details.length > 0 && (
                    <div className="panel__muted">{ai.details[0]}</div>
                  )}
                </div>
              );
            })}
      </div>
      {effectiveTotal > rows && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4, marginBottom: 6 }}>
          <Paginator
            rows={rows}
            totalRecords={effectiveTotal}
            first={first}
            onPageChange={(e) => handlePageChange(e.first)}
            template="PrevPageLink PageLinks NextPageLink"
            className="requests-paginator p-paginator p-component"
          />
        </div>
      )}
      <AddAdditionalInterestDialog
        visible={showAddDialog}
        onHide={() => setShowAddDialog(false)}
        onSave={handleSaved}
        insuredDatabaseId={insuredDatabaseId}
        insuredEmail={insuredEmail}
        insuredFirstName={insuredFirstName}
        insuredLastName={insuredLastName}
        insuredCommercialName={insuredCommercialName}
      />
      <ViewAdditionalInterest
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        interest={selectedInterest}
      />
    </section>
    </>
  );
}
